import ImageService from '@/services/image.service';
import LearnService from '@/services/learn.service';

import { filterByDate } from './filter.js';
import getRetrainModelName from '../../util/retrainModelName.js';
import { showSwal } from '../../util/swal.js';
import i18n from '@/locales/i18n';

import { getField, updateField } from 'vuex-map-fields';
import dayjs from 'dayjs';
import Constants from '../../constants/Constants.js';
import cloneDeep from 'lodash/cloneDeep';

const SET_IMAGES = 'SET_IMAGES';
const SET_ORIGIN_IMAGES = 'SET_ORIGIN_IMAGES';
const SET_IMAGE_COUNT = 'SET_IMAGE_COUNT';
const SET_SKIP = 'SET_SKIP';
const SET_IMAGE_FILTER = 'SET_IMAGE_FILTER';
const RESTORE_RETRAIN_IMAGES = 'RESTORE_RETRAIN_IMAGES';
const SET_NO_IMAGE = 'SET_NO_IMAGE';
const INIT_SKIP = 0;
const LIMIT = 20;

const retrieveImages = async (images) => {
	return Promise.all(
		images
      .map(async (element) => {
				let detectedImageUrl = await ImageService.getPresignedUrl({
					file_name: element.detected_image_name,
					s3_key: element.s3_key_detected,
        });
        
				let rawImageUrl = await ImageService.getPresignedUrl({
					file_name: element.image_name,
					s3_key: element.s3_key,
				});

				let cdnCloudFormation = Constants.CDN_CLOUD_FORMATION.historyImage;
				let cdnCloudFormationRaw = Constants.CDN_CLOUD_FORMATION.runDetection
				let s3BucketUrl = process.env.VUE_APP_S3_BUCKET_URL;

				let cdnImage = detectedImageUrl.data.link.replace(
					s3BucketUrl,
					cdnCloudFormation
				);
				let cdnRawImage = rawImageUrl.data.link.replace(s3BucketUrl, cdnCloudFormation);
				/**
				 * because now, cdn historyImage set size 720x543
				 * has a negative impact on getting reference photos
				 * -> get from runDetection to get with full resolution
				 */
				let cdRawSizeImageURI = rawImageUrl.data.link.replace(s3BucketUrl, cdnCloudFormationRaw);

				element.detectedImageURI = cdnImage;
				element.rawImageURI = cdnRawImage;
				element.rawSizeImageURI = cdRawSizeImageURI;
				element.isRetraining = false;
				element.imageType = 'raw'
				// if element is uploaded from device then set URI to raw
				if (element.is_detected == false) {
					element.detectedImageURI = cdnRawImage
				}
					
				return element;
			})
	);
};

async function getImagesByPagination({ originImages, skip, username, listIdRetrain }) {
	if (listIdRetrain) {
		const listRetrain = listIdRetrain;
		//update the retraining state of the image on scroll
		for (let i = 0; i < originImages.length; i++) {
			for (let j = 0; j < listRetrain.length; j++) {
				if (originImages[i]?._id?.$oid === listRetrain[j].id) {
					originImages[i].isRetraining = listRetrain[j].isRetraining;
				}
			}
		}
	}
	const images = originImages.filter((item, idx) => {
		return idx >= skip && idx < skip + LIMIT;
	});

	const retrieveImagesByLimit = await retrieveImages(images);

	return retrieveImagesByLimit;
}

export default {
	namespaced: true,
	state() {
		return {
			images: [],
			image: null,
			imageURI: '',
			noImage: '',
			listIdRetrain: [],
			allImagesCount: 0,
			originImages: [],
			imageFilter: [],
			skip: INIT_SKIP,
			limit: LIMIT,
			retrainingImages: [],
			// dateStart: null,

			dateStart: dayjs(new Date())
				.set('hour', 0)
				.set('minute', 0)
				.set('second', 0)
				.format('YYYY/MM/DD HH:mm:ss'),
			dateEnd: dayjs(new Date())
				.set('hour', 23)
				.set('minute', 59)
				.set('second', 59)
				.format('YYYY/MM/DD HH:mm:ss'),
		};
	},

	actions: {
		async retrieveAllImages(
			{ commit, state, dispatch },
			{ username, modelName, dateStart, dateEnd }
		) {
			let response = await ImageService.getAllDetectedImages({
				username: username,
				model_name: modelName,
				start_time: dayjs(dateStart).unix(),
				end_time: dayjs(dateEnd).unix(),
			});

			const originImages = response?.data?.all_detected_image_info;
			originImages.sort(function (a, b) {
				// Turn your strings into dates, and then subtract them
				// to get a value that is either negative, positive, or zero.
				return new Date(b.created_at) - new Date(a.created_at);
			});

			commit(SET_ORIGIN_IMAGES, originImages);
			commit(SET_IMAGE_COUNT, originImages?.length);
			commit(RESTORE_RETRAIN_IMAGES);

			dispatch('getImagesInit', {
				originImages: originImages,
				skip: INIT_SKIP,
				username: username,
				modelName: modelName,
			});
		},

		async getImagesInit(
			{ commit, state },
			{ originImages, skip, username, modelName, listIdRetrain }
		) {
	
			const retrievedImages = await getImagesByPagination({
				originImages,
				skip,
				username,
				modelName,
				listIdRetrain,
			});
			if (retrievedImages.length <= 0) {
				commit(SET_NO_IMAGE, true);
			} else {
				commit(SET_NO_IMAGE, false);
			}
			commit(SET_SKIP, skip + LIMIT);

			const allRetrieveImages = await retrieveImages(state.originImages);
			commit(SET_ORIGIN_IMAGES, allRetrieveImages);

			const listRetrain = state.listIdRetrain;
			let temp = [];

			//update the retraining state of the image
			for (let i = 0; i < state.originImages.length; i++) {
				for (let j = 0; j < listRetrain.length; j++) {
					if (state.originImages[i]?._id?.$oid === listRetrain[j].id) {
						state.originImages[i].isRetraining = listRetrain[j].isRetraining;
						temp = [...temp, state.originImages[i]];
					}
				}
			}
			state.retrainingImages = temp;

			commit(SET_IMAGES, retrievedImages);
		},

		async getImagesPaginated({ commit, state }, { username, modelName }) {
			const retrievedImages = await getImagesByPagination({
				originImages: state.originImages,
				skip: state.skip,
				username,
				modelName,
				listIdRetrain: state.listIdRetrain,
			});
			let resultFilterByDate = filterByDate(
				state.dateStart,
				state.dateEnd,
				retrievedImages
			);

			const tempImages = [...state.images].concat(resultFilterByDate);

			commit(SET_SKIP, state.skip + LIMIT);
			commit(SET_IMAGES, tempImages);
		},

		async sortImageByDate(
			{ commit, state, dispatch },
			{ username, modelName, dateStart, dateEnd }
		) {
			await dispatch('retrieveAllImages', {
				username: username,
				modelName: modelName,
				dateStart: dateStart,
				dateEnd: dateEnd,
			});
			state.originImages.sort(function (a, b) {
				// Turn your strings into dates, and then subtract them
				// to get a value that is either negative, positive, or zero.
				return new Date(b.created_at) - new Date(a.created_at);
			});

			let resultFilterByDate = filterByDate(
				state.dateStart,
				state.dateEnd,
				state.originImages
			);
			commit(SET_IMAGE_FILTER, resultFilterByDate);

			dispatch('getImagesInit', {
				originImages: resultFilterByDate,
				skip: INIT_SKIP,
				username: username,
				modelName: modelName,
			});
		},
		async sortImageByName(
			{ commit, state, dispatch },
			{ username, modelName, searchKey }
		) {
			let arrByName = state.originImages.filter((image) => {
				return image?.image_name?.includes(searchKey);
			});
			let resultFilterByDate = filterByDate(state.dateStart, state.dateEnd, arrByName);
			commit(SET_IMAGE_FILTER, resultFilterByDate);

			dispatch('getImagesInit', {
				originImages: resultFilterByDate,
				skip: INIT_SKIP,
				username: username,
				modelName: modelName,
			});
		},

		sortImageByDateRevert({ commit, state, dispatch }, { username, modelName }) {
			dispatch('retrieveAllImages', {
				username: username,
				modelName: modelName,
				dateStart: dateStart,
				dateEnd: dateEnd,
			});
			state.originImages.sort(function (a, b) {
				// Turn your strings into dates, and then subtract them
				// to get a value that is either negative, positive, or zero.
				return new Date(a.created_at) - new Date(b.created_at);
			});

			let resultFilterByDate = filterByDate(
				state.dateStart,
				state.dateEnd,
				state.originImages
			);
			commit(SET_IMAGE_FILTER, resultFilterByDate);
			dispatch('getImagesInit', {
				originImages: resultFilterByDate,
				skip: INIT_SKIP,
				username: username,
				modelName: modelName,
			});
		},

		filterImageByResultOK({ commit, state, dispatch }, { username, modelName }) {
			const arrOk = state.originImages.filter(function (item) {
				// Turn your strings into dates, and then subtract them
				// to get a value that is either negative, positive, or zero.
				return parseInt(item.class) === 0;
			});
			let resultFilterByDate = filterByDate(state.dateStart, state.dateEnd, arrOk);
			commit(SET_IMAGE_FILTER, resultFilterByDate);

			dispatch('getImagesInit', {
				originImages: resultFilterByDate,
				skip: INIT_SKIP,
				username: username,
				modelName: modelName,
			});
		},
		filterImageByResultNG({ commit, state, dispatch }, { username, modelName }) {
			const arrNg = state.originImages.filter(function (item) {
				// Turn your strings into dates, and then subtract them
				// to get a value that is either negative, positive, or zero.
				return parseInt(item.class) === 1;
			});
			let resultFilterByDate = filterByDate(state.dateStart, state.dateEnd, arrNg);
			commit(SET_IMAGE_FILTER, resultFilterByDate);

			dispatch('getImagesInit', {
				originImages: resultFilterByDate,
				skip: INIT_SKIP,
				username: username,
				modelName: modelName,
			});
		},

		setIsTraning({ commit }, { id, isRetraining }) {
			commit('SET_ORIGIN_RETRAIN', { id, isRetraining });
			commit('SET_RETRAIN', { id, isRetraining });
		},

		async retrain({ commit, state, rootState }) {
			try {
				// const templateName = 'holes_patchcore-20230714_023205.pytorch'
				const templateName = rootState?.history?.model?.model;
				const newModelName = getRetrainModelName(templateName);

				const body = {
					username: rootState?.global?.auth?.username,
					template_name: templateName,
					retrain_images: state.retrainingImages.map((item) => {
						return item?.s3_key;
					}),
					model_name: newModelName,
				};
				console.log({ body });
				const res = await LearnService.retrain(body);

				showSwal({
					type: 'success',
					title: i18n.t('success'),
					text: i18n.t('successMessage'),
				});
				// Reset retrainingImages
				commit(RESTORE_RETRAIN_IMAGES);
				// Reset isRetraining of images raw
				state.images.forEach((item) => {
					item.isRetraining = false;
				});
			} catch (error) {
				console.log(error);
				showSwal({
					type: 'fail',
					title: i18n.t('fail'),
					text: i18n.t('failMessage'),
				});
			}
		},
	},

	mutations: {
		updateField,
		SET_ORIGIN_IMAGES(state, payload) {
			state.originImages = payload;
		},

		SET_IMAGES(state, payload) {
			state.images = payload;
		},

		SET_IMAGE_COUNT(state, payload) {
			state.allImagesCount = payload;
		},

		SET_SKIP(state, payload) {
			state.skip = payload;
		},

		SET_ORIGIN_RETRAIN(state, { id, isRetraining }) {
			const images = cloneDeep(state.images);
			const image = images.find((i) => i._id.$oid === id);
			if (image) image.isRetraining = isRetraining;
			state.images = images;
			state.retrainingImages = state.originImages.filter((image) => image.isRetraining);
		},

		SET_RETRAIN(state, { id, isRetraining }) {
			const images = cloneDeep(state.images);
			const imageIdx = images.findIndex((item) => item?._id?.$oid === id);
			if (imageIdx >= 0) {
				// vue.set(state.images[imageIdx], 'isRetraining', isRetraining)
				state.images = images;
			}
		},
		SET_IMAGE_FILTER(state, payload) {
			state.imageFilter = payload;
		},
		SET_DATE_START(state, payload) {
			state.dateStart = payload;
		},
		SET_DATE_END(state, payload) {
			state.dateEnd = payload;
		},
		RESTORE_RETRAIN_IMAGES(state) {
			state.retrainingImages = [];
		},

		REMOVE_RETRAIN_IMAGE(state, { id }) {
			const imageIdx = state.retrainingImages.findIndex(
				(item) => item?._id?.$oid === id
			);
			state.retrainingImages.splice(imageIdx, 1);

			const originImages = state.originImages;

			for (let i = 0; i < originImages.length; i++) {
				if (originImages[i]._id?.$oid === id) {
					originImages[i].isRetraining = false;
				}
			}
		},
		SET_NO_IMAGE(state, payload) {
			state.noImage = payload;
		},
		SET_LIST_ID_RETRAIN(state, payload) {
			state.listIdRetrain = [...state.listIdRetrain, payload];
		},
		SET_REMOVE_LIST_RETRAIN(state, payload) {
			state.listIdRetrain = state.listIdRetrain.filter(
				(item, idx) => item.id != payload
			);
		},
		SET_SELECT_FILTER(state, payload) {
			state.selectFilter = payload;
		},
	},

	getters: {
		getField,

		/**
		 * get train type images
		 * @param {Object} state
		 * @returns {Array} array of train type images
		 */
		trainTypeImages(state) {
			return state.images.filter((image) => image.imageType === 'train');
		},

		/**
		 * get validation type images array
		 * @param {Object} state
		 * @returns {Array} array of validation type images
		 */
		validationTypeImages(state) {
			return state.images.filter((image) => image.imageType === 'validation');
		},
	},
};
